import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Select,
  Button,
  Card,
  Space,
  DatePicker,
  Input,
  Collapse,
  Switch,
  Alert,
  Typography,
  Row,
  Col,
  Divider
} from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  MunicipalityAccessLevel,
  MunicipalityPermissions,
  getPermissionPreset,
  getAccessLevelLabel,
  validatePermissions,
  ExtendedUser
} from '@municipality/types';
import { useMunicipalities } from '../hooks/useMunicipalities';
import { useAuth } from '../hooks/useAuth';
import { canAssignToMunicipality } from '@municipality/types';
import type { Dayjs } from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Text, Title } = Typography;

interface MunicipalityAccessManagerProps {
  onGrantAccess: (
    municipalityIds: string[],
    accessLevel: MunicipalityAccessLevel,
    permissions: MunicipalityPermissions,
    options?: {
      expiresAt?: Date;
      notes?: string;
    }
  ) => Promise<void>;
  loading?: boolean;
}

const MunicipalityAccessManager: React.FC<MunicipalityAccessManagerProps> = ({
  onGrantAccess,
  loading = false
}) => {
  const { t } = useTranslation('users');
  const { municipalities, loading: municipalitiesLoading } = useMunicipalities();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [permissions, setPermissions] = useState<MunicipalityPermissions>(
    getPermissionPreset(MunicipalityAccessLevel.CONTENT_MANAGER)
  );
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  // Filter municipalities based on user's role (Region Managers can only see their assigned municipalities)
  const availableMunicipalities = municipalities.filter((municipality) => {
    if (!user || !user.superAdminRole) return false;
    return canAssignToMunicipality(user as unknown as ExtendedUser, municipality.id);
  });

  const handleAccessLevelChange = (value: MunicipalityAccessLevel) => {
    const preset = getPermissionPreset(value);
    setPermissions(preset);
    form.setFieldsValue({ permissions: preset });
    
    // Validate permissions
    const validation = validatePermissions(preset);
    setValidationWarnings(validation.warnings);
  };

  const handlePermissionChange = (key: keyof MunicipalityPermissions, checked: boolean) => {
    const updatedPermissions = { ...permissions, [key]: checked };
    setPermissions(updatedPermissions);
    
    // Validate permissions
    const validation = validatePermissions(updatedPermissions);
    setValidationWarnings(validation.warnings);
  };

  const handleSubmit = async (values: {
    municipalityIds: string[];
    accessLevel: MunicipalityAccessLevel;
    expiresAt?: Dayjs;
    notes?: string;
  }) => {
    try {
      await onGrantAccess(
        values.municipalityIds,
        values.accessLevel,
        permissions,
        {
          expiresAt: values.expiresAt?.toDate(),
          notes: values.notes,
        }
      );
      
      // Reset form
      form.resetFields();
      setPermissions(getPermissionPreset(MunicipalityAccessLevel.CONTENT_MANAGER));
      setShowCustomPermissions(false);
      setValidationWarnings([]);
    } catch (error) {
      console.error('Error granting access:', error);
    }
  };

  const accessLevelOptions = [
    {
      value: MunicipalityAccessLevel.READ_ONLY,
      label: getAccessLevelLabel(MunicipalityAccessLevel.READ_ONLY),
      description: t('accessLevels.readOnlyDescription', 'View-only access to analytics'),
      color: '#8c8c8c',
    },
    {
      value: MunicipalityAccessLevel.CONTENT_MANAGER,
      label: getAccessLevelLabel(MunicipalityAccessLevel.CONTENT_MANAGER),
      description: t('accessLevels.contentManagerDescription', 'Manage content, surveys, events (no chat)'),
      color: '#1890ff',
    },
    {
      value: MunicipalityAccessLevel.FULL_ACCESS,
      label: getAccessLevelLabel(MunicipalityAccessLevel.FULL_ACCESS),
      description: t('accessLevels.fullAccessDescription', 'Full access including chat and settings'),
      color: '#52c41a',
    },
  ];

  const permissionsList: { key: keyof MunicipalityPermissions; label: string; category: string }[] = [
    // Content Management
    { key: 'canEditNews', label: t('permissions.canEditNews', 'Edit news'), category: 'content' },
    { key: 'canManageEvents', label: t('permissions.canManageEvents', 'Manage events'), category: 'content' },
    { key: 'canManageSurveys', label: t('permissions.canManageSurveys', 'Manage surveys'), category: 'content' },
    { key: 'canManagePOIs', label: t('permissions.canManagePOIs', 'Manage points of interest'), category: 'content' },
    { key: 'canManageVolunteering', label: t('permissions.canManageVolunteering', 'Manage volunteering'), category: 'content' },
    { key: 'canManageLostAndFound', label: t('permissions.canManageLostAndFound', 'Manage lost and found'), category: 'content' },
    { key: 'canManageIssues', label: t('permissions.canManageIssues', 'Manage issues'), category: 'content' },
    
    // Governance
    { key: 'canManageCouncil', label: t('permissions.canManageCouncil', 'Manage council'), category: 'governance' },
    { key: 'canManageMayor', label: t('permissions.canManageMayor', 'Manage mayor'), category: 'governance' },
    { key: 'canManageCommittees', label: t('permissions.canManageCommittees', 'Manage committees'), category: 'governance' },
    
    // Operations
    { key: 'canAccessChat', label: t('permissions.canAccessChat', 'Access chat'), category: 'operations' },
    { key: 'canManageTasks', label: t('permissions.canManageTasks', 'Manage tasks'), category: 'operations' },
    { key: 'canManageSocialMedia', label: t('permissions.canManageSocialMedia', 'Manage social media'), category: 'operations' },
    { key: 'canManageOnlinePayments', label: t('permissions.canManageOnlinePayments', 'Manage online payments'), category: 'operations' },
    { key: 'canManagePushNotifications', label: t('permissions.canManagePushNotifications', 'Manage push notifications'), category: 'operations' },
    
    // Administration
    { key: 'canManageUsers', label: t('permissions.canManageUsers', 'Manage users'), category: 'administration' },
    { key: 'canEditMunicipalitySettings', label: t('permissions.canEditMunicipalitySettings', 'Edit municipality settings'), category: 'administration' },
    
    // Analytics
    { key: 'canViewAnalytics', label: t('permissions.canViewAnalytics', 'View analytics'), category: 'analytics' },
  ];

  const permissionsByCategory = {
    content: permissionsList.filter(p => p.category === 'content'),
    governance: permissionsList.filter(p => p.category === 'governance'),
    operations: permissionsList.filter(p => p.category === 'operations'),
    administration: permissionsList.filter(p => p.category === 'administration'),
    analytics: permissionsList.filter(p => p.category === 'analytics'),
  };

  return (
    <Card title={<Title level={5}>{t('grantMunicipalityAccess', 'Grant municipality access')}</Title>}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          accessLevel: MunicipalityAccessLevel.CONTENT_MANAGER,
        }}
      >
        <Form.Item
          name="municipalityIds"
          label={t('municipalities', 'Municipalities')}
          rules={[{ required: true, message: t('selectAtLeastOneMunicipality', 'Select at least one municipality') }]}
          extra={t('selectMultipleMunicipalities', 'You can select multiple municipalities for bulk assignment')}
        >
          <Select
            mode="multiple"
            placeholder={t('selectMunicipalities', 'Select municipalities')}
            loading={municipalitiesLoading}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={availableMunicipalities.map((m) => ({
              label: m.name,
              value: m.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="accessLevel"
          label={t('accessLevel', 'Access level')}
          rules={[{ required: true }]}
        >
          <Select
            placeholder={t('selectAccessLevel', 'Select access level')}
            onChange={handleAccessLevelChange}
            optionLabelProp="label"
          >
            {accessLevelOptions.map((option) => (
              <Option key={option.value} value={option.value} label={option.label}>
                <div>
                  <div style={{ fontWeight: 500, color: option.color }}>{option.label}</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>{option.description}</div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {validationWarnings.length > 0 && (
          <Alert
            message={t('permissionWarnings', 'Permission warnings')}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Collapse
          ghost
          activeKey={showCustomPermissions ? ['permissions'] : []}
          onChange={(keys) => setShowCustomPermissions(keys.includes('permissions'))}
        >
          <Panel
            header={t('customizePermissions', 'Customize permissions (optional)')}
            key="permissions"
            extra={<InfoCircleOutlined />}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {Object.entries(permissionsByCategory).map(([category, perms]) => (
                <div key={category}>
                  <Text strong style={{ textTransform: 'capitalize' }}>
                    {t(`permissionCategories.${category}`, category)}
                  </Text>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row gutter={[16, 8]}>
                    {perms.map((perm) => (
                      <Col span={12} key={perm.key}>
                        <Space>
                          <Switch
                            checked={permissions[perm.key]}
                            onChange={(checked) => handlePermissionChange(perm.key, checked)}
                            size="small"
                          />
                          <Text>{perm.label}</Text>
                        </Space>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Space>
          </Panel>
        </Collapse>

        <Form.Item
          name="expiresAt"
          label={t('expirationDate', 'Expiration date (optional)')}
          extra={t('leaveBlankForNoExpiration', 'Leave blank for no expiration')}
          style={{ marginTop: 16 }}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('notes', 'Notes (optional)')}
          extra={t('internalNotes', 'Internal notes about this assignment')}
        >
          <TextArea rows={3} placeholder={t('addNotes', 'Add notes...')} />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<PlusOutlined />}
            block
          >
            {t('grantAccess', 'Grant access')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default MunicipalityAccessManager;
